import React, { useEffect, useState } from 'react';
import { Container, Spinner, Card, Table, Row, Col } from 'react-bootstrap';
import { DataManager } from '../core/data-manager';
import { ApiManager } from '../core/api-manager';
import { toast } from 'react-toastify';

import { PieChart } from '@carbon/charts-react';
import '@carbon/charts/styles.css';


export default function Dashboard() {

    const [dataPie, setDataPie] = useState(null);
    const [dataSource, setDataSource] = useState(null);
    const [dataCountry, setDataCountry] = useState(null);
    const [dataSector, setDataSector] = useState(null);
    const [dataRole, setDataRole] = useState(null);
    const [dataAgency, setDataAgency] = useState(null);

    const [errPie, setErrPie] = useState(false);
    const [errSource, setErrSource] = useState(false);
    const [errCountry, setErrCountry] = useState(false);
    const [errSector, setErrSector] = useState(false);
    const [errRole, setErrRole] = useState(false);
    const [errAgency, setErrAgency] = useState(false);

    useEffect(() => {
        getJobPost();
        getDataSource();
        getDataAgency();
        getDataCountry();
        getDataSector();
        getDataRole();
    }, []);

    const baseChartOptions = (title) => ({
        title,
        resizable: true,
        height: '400px',
        legend: { alignment: 'center' },
        pie: { alignment: 'center' }
    });

    function colorFromString(str) {
        try {
            let hash = 0;
            for (let i = 0; i < String(str).length; i++) {
                hash = String(str).charCodeAt(i) + ((hash << 5) - hash);
            }
            const hue = Math.abs(hash) % 360;
            return `hsl(${hue}, 65%, 55%)`;
        } catch (e) {
            return '#888888';
        }
    }

    function optionsWithColors(baseOptions, dataArray) {
        if (!Array.isArray(dataArray) || dataArray.length === 0) return baseOptions;
        const scale = {};
        dataArray.forEach(d => {
            const key = d && (d.group ?? d.label ?? d.name);
            if (key !== undefined && scale[key] === undefined) {
                scale[key] = colorFromString(key);
            }
        });
        return { ...baseOptions, color: { scale } };
    }

    const sectorPalette = ['#1f77b4', '#2ca02c', '#ff7f0e', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

    function optionsWithPalette(baseOptions, dataArray, palette) {
        if (!Array.isArray(dataArray) || dataArray.length === 0) return baseOptions;
        const scale = {};
        dataArray.forEach((d, i) => {
            const key = d && (d.group ?? d.label ?? d.name);
            if (key !== undefined && scale[key] === undefined) {
                scale[key] = palette[i % palette.length];
            }
        });
        return { ...baseOptions, color: { scale } };
    }

    async function getJobPost() {
        try {
            const sorting = { field: 'id', order: 'desc' };
            const res = await DataManager.getItems('job_post', 1, 10000, sorting);
            if (res?.success === 1) {
                const items = res.body || [];
                const countPending = items.filter(i => i?.status?.value === '0_DRAFT').length;
                const countOpen = items.filter(i => i?.status?.value === '10_OPEN').length;
                const countClosed = items.filter(i => i?.status?.value === '20_CLOSED').length;
                setDataPie([
                    { group: 'In bozza', value: countPending },
                    { group: 'Aperte', value: countOpen },
                    { group: 'Chiuse', value: countClosed }
                ]);
            } else {
                throw new Error(res?.message || 'risposta non valida');
            }
        } catch (err) {
            console.error('getJobPost error', err);
            setErrPie(true);
            setDataPie([]);
            toast.error('Errore nel caricamento delle posizioni.', { theme: 'colored' });
        }
    }

    async function fetchCustomStat(action, setter, setError, label) {
        try {
            const res = await ApiManager.sendAuthenticatedRequest('/custom/' + action, {});
            if (res?.success === 1) {
                setter(Array.isArray(res.body) ? res.body : []);
            } else {
                throw new Error(res?.message || 'risposta non valida');
            }
        } catch (err) {
            console.error(action + ' error', err);
            setError(true);
            setter([]);
            toast.error('Errore nel caricamento: ' + label + '.', { theme: 'colored' });
        }
    }

    const getDataSource = () => fetchCustomStat('get_data_source', setDataSource, setErrSource, 'fonti candidature');
    const getDataCountry = () => fetchCustomStat('get_data_country', setDataCountry, setErrCountry, 'nazioni');
    const getDataSector = () => fetchCustomStat('get_data_sector', setDataSector, setErrSector, 'settori');
    const getDataRole = () => fetchCustomStat('get_data_role', setDataRole, setErrRole, 'ruoli');
    const getDataAgency = () => fetchCustomStat('get_data_agency', setDataAgency, setErrAgency, 'agenzie');

    function ChartCol({ title, data, error, options }) {
        return (
            <Col>
                <Card className='p-3' style={{ minHeight: 460 }}>
                    {data === null ? (
                        <div className='d-flex flex-column align-items-center justify-content-center text-muted h-100'>
                            <Spinner animation='border' size='sm' className='mb-2' />
                            <small>Caricamento {title}...</small>
                        </div>
                    ) : error ? (
                        <div className='d-flex align-items-center justify-content-center text-danger small h-100'>
                            Errore nel caricamento.
                        </div>
                    ) : data.length === 0 ? (
                        <div className='d-flex align-items-center justify-content-center text-muted small h-100'>
                            Nessun dato disponibile.
                        </div>
                    ) : (
                        <PieChart data={data} options={options} />
                    )}
                </Card>
            </Col>
        );
    }

    return (
        <Container>
            <Row className='my-5'>
                <ChartCol title='posizioni' data={dataPie} error={errPie} options={optionsWithColors(baseChartOptions('Posizioni totali'), dataPie)} />
                <ChartCol title='nazioni' data={dataCountry} error={errCountry} options={optionsWithColors(baseChartOptions('Nazioni'), dataCountry)} />
                <ChartCol title='settori' data={dataSector} error={errSector} options={optionsWithPalette(baseChartOptions('Settore'), dataSector, sectorPalette)} />
                <ChartCol title='ruoli' data={dataRole} error={errRole} options={optionsWithColors(baseChartOptions('Ruolo'), dataRole)} />
            </Row>
            <Row className='my-5'>
                <ChartCol title='fonti' data={dataSource} error={errSource} options={optionsWithColors(baseChartOptions('Fonti candidature'), dataSource)} />
                <Col>
                    <h4>Elenco agenzie</h4>
                    {dataAgency === null ? (
                        <div className='text-muted small d-flex align-items-center'>
                            <Spinner animation='border' size='sm' className='me-2' />
                            Caricamento...
                        </div>
                    ) : errAgency ? (
                        <div className='text-danger small'>Errore nel caricamento.</div>
                    ) : dataAgency.length === 0 ? (
                        <div className='text-muted small'>Nessun dato disponibile.</div>
                    ) : (
                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>Agenzia</th>
                                    <th>Numero candidati</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataAgency.map((item, i) => (
                                    <tr key={i}>
                                        <td>{item.group}</td>
                                        <td>{item.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Col>
            </Row>
        </Container>
    );
};
